import { AlertCircle, KeyRound, ShieldCheck } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";

export interface ResetPasswordFormProps {
  /** The token from the reset email's `?token=` query param. */
  token: string;
  /** Rendered after a successful reset (e.g. a sign-in link). */
  successSlot?: ReactNode;
  minLength?: number;
}

/** Sets a new password from an emailed reset link. */
export const ResetPasswordForm = ({
  token,
  successSlot,
  minLength = 8,
}: ResetPasswordFormProps) => {
  const client = useUmClient();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatched = confirm.length > 0 && confirm !== next;
  const tooShort = next.length > 0 && next.length < minLength;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatched || tooShort) return;
    setLoading(true);
    setError(null);
    const res = await client.resetPassword({ token, newPassword: next });
    setLoading(false);
    if (res.ok) {
      setDone(true);
    } else {
      setError(
        errorMessage(
          res.error,
          "This reset link is invalid or has expired. Request a new one."
        )
      );
    }
  };

  if (done) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <ShieldCheck className="size-5 text-green-600" />
            Password updated
          </CardTitle>
          <CardDescription>
            Your password has been changed. You can sign in with it now.
          </CardDescription>
        </CardHeader>
        {successSlot ? <CardContent>{successSlot}</CardContent> : null}
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1.5">
        <CardTitle className="flex items-center gap-2 text-2xl">
          <KeyRound className="size-5 text-muted-foreground" />
          Choose a new password
        </CardTitle>
        <CardDescription>
          Enter a new password for your account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field
            label="New password"
            htmlFor="reset-pw"
            required
            description={`At least ${minLength} characters.`}
            error={tooShort ? "New password is too short." : undefined}
          >
            <Input
              id="reset-pw"
              type="password"
              autoComplete="new-password"
              required
              autoFocus
              value={next}
              onChange={(e) => setNext(e.target.value)}
              aria-invalid={tooShort || undefined}
            />
          </Field>
          <Field
            label="Confirm new password"
            htmlFor="reset-pw-confirm"
            required
            error={mismatched ? "Passwords don't match." : undefined}
          >
            <Input
              id="reset-pw-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={mismatched || undefined}
            />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={mismatched || tooShort || !next}
          >
            Set new password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
